import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { Button } from "iuvity-ui-components";
import { TranslationsImporter } from "../../components/translation/TranslationsImporter";
import { getGlobalConfigurationPageLabel } from "../../utils";
import { LabelObject } from "../../interfaces";
import { AppContext } from "../../context/index";
import { useValidationMessages } from "../../hooks/useValidationMessages";
import { Column } from 'primereact/column'
import { InputText } from "primereact/inputtext";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";


const filterDefaultValues = {
  functionality: [],
  code: '',
  ibmessage: '',
  description: '',
  technicaldescription: '',
  language: '',
  version: [],
};

export const ValidationMessagesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedApp, selectedLabel, pageFilter, language },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          functionality: pageFilter?.functionality || [],
          code: pageFilter?.code || '',
          ibmessage: pageFilter?.ibmessage || '',
          description: pageFilter?.description || '',
          technicaldescription: pageFilter?.technicaldescription || '',
          language: pageFilter?.language || '',
          version: pageFilter?.version || [],
        }
      : filterDefaultValues,
  });

  const { data, validationMessagesAction, reloadFn } = useValidationMessages("DATA");
  const [filteredData, setFilteredData] = useState<any[] | null>(null);
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const { control, setValue } = form;

  const customPageLabel = getGlobalConfigurationPageLabel(t, location.pathname);

  const columns = useMemo(() => {
    return [
      {
        field: "functionality",
        header: t("ValidationMessages.functionalityLabel"),
      },
      {
        field: "code",
        header: t("ValidationMessages.codeLabel"),
      },
      {
        field: "ibmessage",
        header: t("ValidationMessages.ibmessageLabel"),
      },
      {
        field: "description",
        header: t("ValidationMessages.descriptionLabel"),
      },
      {
        field: "technicaldescription",
        header: t("ValidationMessages.technicaldescriptionLabel"),
      },
      {
        field: "language",
        header: t("ValidationMessages.languageLabel"),
      },
      {
        field: "releaseversion",
        header: t("ValidationMessages.releaseversionLabel"),
      },
      {
        field: "actions",
        header: t("ValidationMessages.actionsLabel"),
      },
    ];
  }, [t]);

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    functionality: { value: null, matchMode: FilterMatchMode.CONTAINS },
    code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ibmessage: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    technicaldescription: { value: null, matchMode: FilterMatchMode.CONTAINS },
    language: { value: null, matchMode: FilterMatchMode.CONTAINS },
    releaseversion: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const actionEditFn = (label: LabelObject) => {
    setAppState((prev) => ({
      ...prev,
      selectedValidationMessagesLabel: label,
    }));
    navigate(`${location.pathname}/edit`);
    window.scrollTo(0, 0);
  };

  const onDownloadTranslations = async () => {
    const matchKeys = {
      functionality: "widgets",
      type: "type",
      version: "releaseversion",
    };
    const itemsToFilter = Object.fromEntries(
      Object.entries(form.getValues())
        .filter(([_, value]) => value.length > 0)
        .map(([key, value]) => [(matchKeys as any)[key], value])
    );
    await validationMessagesAction("DOWNLOAD", {
      appid: selectedApp?.appid,
      filename: `${selectedApp?.tenantname
        .replaceAll(" ", "")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase())}_${selectedApp?.appname
        .replaceAll(" ", "")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase())}_${selectedApp?.env.slice(
        0,
        3
      )}_${t("ValidationMessages.translationsTitleFront").replaceAll(" ", "")}_${
        new Date().toISOString().substring(0, 10).replace(/-|:/g, "") +
        "-" +
        new Date().toLocaleTimeString("es-ES").replace(/-|:/g, "")
      }`,
      filtersInfo: Object.keys(itemsToFilter).length > 0 && itemsToFilter,
    });
  };

  const onClearFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      functionality: { value: null, matchMode: FilterMatchMode.CONTAINS },
      code: { value: null, matchMode: FilterMatchMode.CONTAINS },
      ibmessage: { value: null, matchMode: FilterMatchMode.CONTAINS },
      description: { value: null, matchMode: FilterMatchMode.CONTAINS },
      technicaldescription: { value: null, matchMode: FilterMatchMode.CONTAINS },
      language: { value: null, matchMode: FilterMatchMode.CONTAINS },
      releaseversion: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(filterDefaultValues);
  };

  const handleModalProps = async (
    type: "DOWNLOAD" | "IMPORT",
    label?: LabelObject
  ) => {
    switch (type) {
      case "DOWNLOAD":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("ValidationMessages.downloadModalTitle"),
          content: t("ValidationMessages.downloadModalContent"),
          onConfirm: () => onDownloadTranslations(),
        }));

      default:
        return;
    }
  };

  useEffect(() => {
    if (data && !pageFilter) {
      setFilteredData(data);
    } else if (!data) {
      setFilteredData(null);
    }
  }, [data, setFilteredData, setAppState, selectedLabel, pageFilter]);

  useLayoutEffect(() => {
    if (data && pageFilter) {
      const filterWidgets = form.getValues("functionality");
      const filterTypes = form.getValues("code");
      const filterVersion = form.getValues("version");
      setFilteredData(
        data.filter(
          (item: any) =>
            (form.getValues("functionality").length > 0
              ? item.widgets.some(
                  (wid: any[]) => filterWidgets.indexOf(wid) !== -1
                )
              : true) &&
            (form.getValues("code").length > 0
              ? filterTypes.includes(item.type)
              : true) &&
            (form.getValues("ibmessage").length > 0
              ? filterTypes.includes(item.type)
              : true) &&
            (form.getValues("description").length > 0
              ? filterTypes.includes(item.type)
              : true) &&
            (form.getValues("technicaldescription").length > 0
              ? filterTypes.includes(item.type)
              : true) &&
            (form.getValues("language").length > 0
              ? filterTypes.includes(item.type)
              : true) &&
            (form.getValues("version").length > 0
              ? filterVersion.includes(item.releaseversion)
              : true)
        )
      );
    } else if (data) {
      setFilteredData(data);
    }
  }, [data, setFilteredData, form, pageFilter]);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`ValidationMessages.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`ValidationMessages.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`ValidationMessages.${options.field}Placeholder`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  }

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("ValidationMessages.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFilters}
      />
    )
  }
  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("ValidationMessages.applyFilterLabel")}
        className="p-button"
        onClick={options.filterApplyCallback}
      />
    )
  }

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("ValidationMessages.validationMessagesTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={customPageLabel}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => {
              navigate("/");
              onClearFilters();
            },
          }}
          breadcrumbsItems={[
            {
              label: t("ValidationMessages.validationMessagesTitle"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("ValidationMessages.validationMessagesDescription")}</span>
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        className="multiactions-c"
        actions={[
          {
            id: "222",
            icon: "iu iu-icon-download",
            label: t("ValidationMessages.downloadBtnLabel"),
            onClick: () => handleModalProps("DOWNLOAD"),
            style: { width: "10rem !important" },
            disabled: !!(filteredData && filteredData?.length === 0) || !data,
          },
          {
            id: "333",
            icon: "iu iu-icon-upload",
            label: t("ValidationMessages.importTranslationsTitle"),
            onClick: () => setImportModalVisible(true),
            style: { width: "10rem !important" },
            disabled: !data,
          },
        ]}
      />

      <BoxFull className="mt-2 py-0 px-0" variant="tertiary">
        <div className="tablefilter-mc">
          <Button
            label={t("ValidationMessages.clearFilterBtnLabel")}
            variant="p-button"
            icon="iu iu-icon-error"
            onClick={onClearFilters}
          />
        </div>
      </BoxFull>

      <BoxFull className="dt-c my-4">
        {data ? (
          <DataTable
            header={
              <TableHeader
                title={t("ValidationMessages.validationMessagesTitleTable")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            editMode="row"
            dataKey="id"
            sortField="functionality"
            sortOrder={1}
            filters={filters}
            value={filteredData?.map((label: any) => ({
              ...label,
              functionality: label.widgets.join(", "),
              // type: t(`ValidationMessages.${label.type.toLowerCase()}OptionLabel`),
              actions: (
                <Actions
                  enableDelete={label.isCustom}
                  onEdit={() => actionEditFn(label)}
                />
              ),
            }))}
            scrollable
            resizableColumns
            globalFilterFields={['functionality', 'code', 'ibmessage', 'description', 'technicaldescription', 'language', 'releaseversion']}
            filterDisplay="menu"
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? { justifyContent: "center" }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field === "actions") ? "8rem" : "14rem",
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={col.field === "actions" ? false : true}
                filterElement={filterTemplate}
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'isCustom')}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      {data && importModalVisible && (
        <TranslationsImporter
          allowedIDs={data.map((label: any) => `${label.id}`)}
          columns={[
            {
              field: "id",
              header: "id",
            },
            ...columns
              .filter((col: any) => col?.field !== "actions")
              .map((col) => ({
                ...col,
                field: col?.field === "releaseversion" ? "version" : col?.field,
              })),
            {
              field: "description",
              header: t("Translations.descriptionLabel"),
            },
          ]}
          importFn={validationMessagesAction}
          reloadFn={reloadFn}
          setVisible={setImportModalVisible}
          auxModalController={setModalProps}
        />
      )}

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
